import { useState } from "react";
import {
  AlertTriangle,
  Package,
  ShoppingCart,
  Gavel,
  Store,
  FileText,
  FlaskConical,
  HeartPulse,
  ClipboardCheck,
  Tag,
  Link2,
  ImageIcon,
  ExternalLink,
  FileDown,
  Loader2,
} from "lucide-react";
import CopyableUrl from "./CopyableUrl";
import {
  formatRecallDate,
  humanize,
  Chip,
  actionMeta,
  RecommendedActionChip,
} from "./takedownUtils";

/* Self-contained expandable text block (avoids importing from the panel file,
   which would create a circular import — same pattern as RecallDetailView). */
const ExpandableText = ({ value, previewLength = 280 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!value) return null;
  const text = String(value);
  const shouldCollapse = text.length > previewLength;
  const display =
    expanded || !shouldCollapse ? text : `${text.slice(0, previewLength).trim()}…`;
  return (
    <p className="text-xs leading-relaxed text-mutedText whitespace-pre-wrap break-words">
      {display}
      {shouldCollapse && (
        <button
          onClick={() => setExpanded((p) => !p)}
          className="ml-1.5 text-xs text-velBlue hover:underline"
        >
          {expanded ? "less" : "more"}
        </button>
      )}
    </p>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-mutedText/80">
      {Icon && <Icon size={12} className="shrink-0" />}
      {title}
    </div>
    {children}
  </div>
);

/* A single discovery listing (URL + provenance + optional price). Shared by the
   US-availability list and the per-vendor enforcement evidence. When the listing
   has its own takedown_shopping doc and an `onOpen` handler, the URL opens that
   doc in the sidebar overlay (like a recall citation); otherwise it's copy-only. */
const ListingRow = ({ listing, onOpen }) => {
  const canOpen = !!onOpen && !!listing?.document_id;
  return (
    <div className="flex flex-col gap-0.5">
      {listing?.price ? (
        <span className="text-[11px] text-mutedText/70">{listing.price}</span>
      ) : null}
      {listing?.url ? (
        canOpen ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen({ document_id: listing.document_id, url: listing.url });
            }}
            title="Open listing"
            className="group flex items-center gap-1 min-w-0 text-left"
          >
            <span className="truncate text-xs text-velBlue/80 group-hover:underline">
              {listing.url}
            </span>
            <ExternalLink
              size={10}
              className="shrink-0 text-mutedText group-hover:text-velBlue"
            />
          </button>
        ) : (
          <CopyableUrl value={listing.url} />
        )
      ) : null}
    </div>
  );
};

/* One store on the enforcement worklist: the vendor domain, the action it needs
   (warning_letter, or second_warning_letter if it was already warned and hasn't
   complied), and its distinct product-page URLs nested as evidence. */
const TargetBlock = ({ target, onOpen }) => {
  const listings = Array.isArray(target?.listings) ? target.listings : [];
  return (
    <div className="rounded-lg border border-surfaceBorder bg-innerBg/50 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <Store size={13} className="shrink-0 text-mutedText" strokeWidth={1.75} />
        <span className="text-xs font-medium text-mainText break-words">
          {target?.vendor || "Unknown vendor"}
        </span>
        <RecommendedActionChip
          action={target?.recommended_action}
          className="ml-auto shrink-0"
        />
      </div>
      {listings.length > 0 && (
        <div className="flex flex-col gap-1.5 pl-5">
          {listings.map((l, i) => (
            <ListingRow key={i} listing={l} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
};

/* Detail view for the shopping-doc side of a takedown ("takedown_shopping") — a
   single confirmed vendor listing, flagged for enforcement. */
const ShoppingDetailView = ({ article, onShowScreenshot }) => {
  const { absolute: dateLabel } = formatRecallDate(article.date_published);
  const previewSrc = article.screenshot || article.screenshot_full;
  const showPreview = !!previewSrc && !!onShowScreenshot;

  return (
    <div className="p-4 flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip color="#f97316">
            <ShoppingCart size={10} className="shrink-0" />
            For Sale
          </Chip>
          <RecommendedActionChip action={article.recommended_action} />
          {dateLabel && (
            <span className="text-[11px] text-mutedText/70">{dateLabel}</span>
          )}
        </div>

        <h2 className="text-sm font-semibold leading-snug text-mainText break-words">
          {article.title || "Vendor listing"}
        </h2>

        <div className="flex items-center gap-1.5 text-xs text-mutedText">
          {article.merchant && <Store size={12} className="shrink-0" />}
          <span className="break-words">{article.merchant || article.source}</span>
          {article.price ? (
            <span className="text-mutedText/70"> · {article.price}</span>
          ) : null}
        </div>

        {article.takedown_action && (
          <span className="text-[11px] text-mutedText/60">
            Flagged via {article.takedown_action}
          </span>
        )}

        {showPreview && (
          <button
            type="button"
            onClick={onShowScreenshot}
            title="View full screenshot"
            className="group relative mt-1 h-32 w-full overflow-hidden rounded-lg border border-surfaceBorder cursor-pointer"
          >
            <img
              src={previewSrc}
              alt="Screenshot preview"
              className="h-full w-full object-cover object-top transition-transform duration-200 group-hover:scale-[1.02]"
              loading="lazy"
              draggable={false}
            />
            <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] text-white/90 backdrop-blur-sm">
              <ImageIcon size={10} className="shrink-0" />
              View full
            </span>
          </button>
        )}

        {article.url && (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[11px] text-mutedText/70">Listing</span>
            <CopyableUrl value={article.url} />
          </div>
        )}
      </div>

      {/* What FDA action this listing was flagged under — context carried from
          the parent takedown so the vendor listing is self-explanatory. */}
      {(article.takedown_product ||
        article.takedown_subject ||
        article.takedown_summary ||
        article.takedown_url) && (
        <Section icon={AlertTriangle} title="FDA takedown">
          <div className="rounded-lg border border-surfaceBorder bg-innerBg/50 p-3 flex flex-col gap-2 min-w-0">
            {article.takedown_product && (
              <div className="flex gap-1.5 text-[11px]">
                <span className="text-mutedText/70 shrink-0">Product:</span>
                <span className="text-mainText break-words">
                  {article.takedown_product}
                </span>
              </div>
            )}
            {article.takedown_subject && (
              <div className="flex gap-1.5 text-[11px]">
                <span className="text-mutedText/70 shrink-0">Subject:</span>
                <span className="text-mutedText break-words">
                  {article.takedown_subject}
                </span>
              </div>
            )}
            {article.takedown_action && (
              <div className="flex gap-1.5 text-[11px]">
                <span className="text-mutedText/70 shrink-0">FDA action:</span>
                <span className="text-mutedText break-words">
                  {article.takedown_action}
                </span>
              </div>
            )}
            {article.takedown_summary && (
              <p className="text-xs leading-relaxed text-mutedText break-words">
                {article.takedown_summary}
              </p>
            )}
            {article.takedown_url && (
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[11px] text-mutedText/70">FDA notice</span>
                <CopyableUrl value={article.takedown_url} />
              </div>
            )}
          </div>
        </Section>
      )}

      {article.content && (
        <Section icon={FileText} title="Listing page text">
          <ExpandableText value={article.content} previewLength={400} />
        </Section>
      )}
    </div>
  );
};

/* Structured detail view for a takedown, rendered in place of the generic JSON
   tree on the panel's "Details" tab. Handles both the FDA-action doc
   ("takedowns") and its linked vendor listings ("takedown_shopping"). */
const TakedownDetailView = ({
  article: rawArticle,
  onShowScreenshot,
  onOpenCitation,
}) => {
  // True while the report PDF is being built, so we can disable + spin the button
  // and block double-clicks. Declared before the early returns below to satisfy the
  // rules of hooks (same pattern as RecallDetailView).
  const [exporting, setExporting] = useState(false);

  // This build nests takedown fields under `meta` (see Feeds/TakedownCard.jsx);
  // current-build emitted them top-level. Hoist meta.* up so the shared reads below
  // (and ShoppingDetailView / TakedownReportPDF, which receive this object) work for
  // either doc shape. Top-level keys win on conflict; a doc with no `meta` passes
  // through unchanged.
  const article =
    rawArticle && rawArticle.meta
      ? { ...rawArticle.meta, ...rawArticle }
      : rawArticle;

  if (!article) return null;

  if (article.target_type === "takedown_shopping") {
    return (
      <ShoppingDetailView article={article} onShowScreenshot={onShowScreenshot} />
    );
  }

  const action = actionMeta(article);
  const { absolute: dateLabel } = formatRecallDate(
    article.date || article.detail_published_date,
  );

  const ingredients = Array.isArray(article.hidden_ingredients)
    ? article.hidden_ingredients
    : [];
  const risks = Array.isArray(article.health_risks) ? article.health_risks : [];
  const violations = Array.isArray(article.violations) ? article.violations : [];
  const upcs = Array.isArray(article.upcs) ? article.upcs : [];
  const lots = Array.isArray(article.lots) ? article.lots : [];
  const firmSites = Array.isArray(article.firm_websites)
    ? article.firm_websites
    : [];
  const linkedPages = Array.isArray(article.linked_pages)
    ? article.linked_pages
    : [];
  const targets = Array.isArray(article.enforcement_targets)
    ? article.enforcement_targets
    : [];

  const hazard = article.subject;
  const previewSrc = article.screenshot || article.screenshot_full;
  const showPreview = !!previewSrc && !!onShowScreenshot;

  const report = article.report;
  const hasReport = !!report && !!report.title_page;

  const handleExportReport = async () => {
    if (exporting || !hasReport) return;
    setExporting(true);
    try {
      // Dynamically import the heavy PDF renderer so it stays out of the takedown
      // detail bundle and only loads when a report is actually generated.
      const [{ pdf }, { default: TakedownReportPDF, buildTakedownFileName }] =
        await Promise.all([
          import("@react-pdf/renderer"),
          import("./TakedownReportPDF"),
        ]);
      const blob = await pdf(
        <TakedownReportPDF report={report} article={article} />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildTakedownFileName({ report, article });
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("[TakedownDetailView] report PDF export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // Product & firm meta rows — render only the fields this family produced.
  const meta = [
    ["Firm", article.firm || article.firm_name],
    ["Brand", article.brand],
    ["Distribution", article.distribution],
    ["Recall status", article.recall_status && humanize(article.recall_status)],
    ["Response due", article.response_deadline],
    ["MARCS-CMS", article.marcs_cms_number],
    ["Court", article.court],
  ].filter(([, v]) => v != null && v !== "");

  return (
    <div className="p-4 flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip color={action.color}>
            <AlertTriangle size={10} className="shrink-0" />
            {action.label}
          </Chip>
          {article.action_category && (
            <Chip>{humanize(article.action_category)}</Chip>
          )}
          {dateLabel && (
            <span className="text-[11px] text-mutedText/70">{dateLabel}</span>
          )}
        </div>

        <h2 className="text-sm font-semibold leading-snug text-mainText break-words">
          {article.product || article.title || "Untitled takedown"}
        </h2>

        {/* The FDA document's own title, when it differs from the product name. */}
        {article.title && article.title !== article.product && (
          <p className="text-[11px] leading-snug text-mutedText/80 break-words">
            {article.title}
          </p>
        )}

        <div className="flex items-center gap-1.5 text-xs text-mutedText">
          <span className="break-words">FDA</span>
          {article.firm && (
            <>
              <span className="text-mutedText/50">·</span>
              <span className="break-words">{article.firm}</span>
            </>
          )}
        </div>

        {showPreview && (
          <button
            type="button"
            onClick={onShowScreenshot}
            title="View full screenshot"
            className="group relative mt-1 h-32 w-full overflow-hidden rounded-lg border border-surfaceBorder cursor-pointer"
          >
            <img
              src={previewSrc}
              alt="Screenshot preview"
              className="h-full w-full object-cover object-top transition-transform duration-200 group-hover:scale-[1.02]"
              loading="lazy"
              draggable={false}
            />
            <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] text-white/90 backdrop-blur-sm">
              <ImageIcon size={10} className="shrink-0" />
              View full
            </span>
          </button>
        )}

        {article.url && (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[11px] text-mutedText/70">Source</span>
            <CopyableUrl value={article.url} />
          </div>
        )}

        {hasReport && (
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleExportReport}
              disabled={exporting}
              title="Download the full grounded health-fraud impact report with references (PDF)"
              className="inline-flex items-center gap-1.5 rounded-md border border-surfaceBorder bg-innerBg px-2.5 py-1.5 text-[11px] font-medium text-mainText transition-colors cursor-pointer hover:border-velBlue/40 hover:text-velBlue disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={12} className="shrink-0 animate-spin" />
              ) : (
                <FileDown size={12} className="shrink-0" />
              )}
              {exporting ? "Generating…" : "Download report"}
            </button>
          </div>
        )}
      </div>

      {/* Hazard / FDA subject */}
      {hazard && (
        <div
          className="flex items-start gap-2 rounded-lg px-3 py-2"
          style={{
            background: `${action.color}14`,
            border: `1px solid ${action.color}33`,
          }}
        >
          <AlertTriangle
            size={14}
            className="mt-0.5 shrink-0"
            style={{ color: action.color }}
          />
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-mutedText/80">
              Subject
            </span>
            <span className="text-xs leading-relaxed text-mainText break-words">
              {hazard}
            </span>
          </div>
        </div>
      )}

      {/* Summary */}
      {article.summary && (
        <Section icon={FileText} title="Summary">
          <p className="text-xs leading-relaxed text-mutedText break-words">
            {article.summary}
          </p>
        </Section>
      )}

      {/* Enforcement worklist — the point of the crawler. Vendors still selling
          the product that need a warning letter, grouped by store domain. */}
      {targets.length > 0 && (
        <Section
          icon={Gavel}
          title={`Vendors needing a warning letter (${targets.length})`}
        >
          <div className="flex flex-col gap-2">
            {targets.map((t, i) => (
              <TargetBlock key={i} target={t} onOpen={onOpenCitation} />
            ))}
          </div>
        </Section>
      )}

      {/* Hidden drug ingredients */}
      {ingredients.length > 0 && (
        <Section icon={FlaskConical} title="Hidden ingredients">
          <div className="flex flex-wrap items-center gap-1.5">
            {ingredients.map((ing, i) => (
              <Chip key={i} color="#f43f5e">
                {ing}
              </Chip>
            ))}
          </div>
        </Section>
      )}

      {/* Health risks */}
      {risks.length > 0 && (
        <Section icon={HeartPulse} title="Health risks">
          <ul className="flex flex-col gap-1">
            {risks.map((r, i) => (
              <li
                key={i}
                className="flex items-start gap-1.5 text-xs leading-relaxed text-mutedText break-words"
              >
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-mutedText/60" />
                {r}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* FDA recommendation */}
      {article.fda_recommendation && (
        <Section icon={ClipboardCheck} title="FDA recommendation">
          <ExpandableText value={article.fda_recommendation} />
        </Section>
      )}

      {/* Product & firm meta */}
      {(meta.length > 0 || upcs.length > 0 || lots.length > 0) && (
        <Section icon={Package} title="Product & firm">
          <div className="rounded-lg border border-surfaceBorder bg-innerBg/50 p-3 flex flex-col gap-2">
            {meta.length > 0 && (
              <div className="flex flex-col gap-0.5">
                {meta.map(([k, v]) => (
                  <div key={k} className="flex gap-1.5 text-[11px]">
                    <span className="text-mutedText/70 shrink-0">{k}:</span>
                    <span className="text-mutedText break-words">{v}</span>
                  </div>
                ))}
              </div>
            )}
            {(upcs.length > 0 || lots.length > 0) && (
              <div className="flex flex-wrap gap-1">
                {upcs.map((u, i) => (
                  <Chip key={`upc-${i}`}>UPC: {u}</Chip>
                ))}
                {lots.map((l, i) => (
                  <Chip key={`lot-${i}`}>Lot: {l}</Chip>
                ))}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Violations (warning letters) */}
      {violations.length > 0 && (
        <Section icon={AlertTriangle} title="Violations">
          <ul className="flex flex-col gap-1">
            {violations.map((v, i) => (
              <li
                key={i}
                className="flex items-start gap-1.5 text-xs leading-relaxed text-mutedText break-words"
              >
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-mutedText/60" />
                {v}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Firm websites (the actioned store's own sites) */}
      {firmSites.length > 0 && (
        <Section icon={Store} title="Firm websites">
          <div className="flex flex-col gap-1">
            {firmSites.map((s, i) => (
              <CopyableUrl key={i} value={s} />
            ))}
          </div>
        </Section>
      )}

      {/* Related FDA pages */}
      {linkedPages.length > 0 && (
        <Section icon={Link2} title="Related FDA pages">
          <div className="flex flex-col gap-1.5">
            {linkedPages.map((p, i) => (
              <div key={i} className="flex flex-col gap-0.5">
                {(p?.type || p?.label) && (
                  <div className="flex items-center gap-1.5">
                    <Tag size={9} className="shrink-0 text-mutedText/70" />
                    <span className="text-[11px] text-mutedText">
                      {p.label || humanize(p.type)}
                    </span>
                  </div>
                )}
                {(p?.url || typeof p === "string") && (
                  <CopyableUrl value={p.url || p} />
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Source page text */}
      {article.content && (
        <Section icon={FileText} title="Source page text">
          <ExpandableText value={article.content} previewLength={400} />
        </Section>
      )}
    </div>
  );
};

export default TakedownDetailView;
